import React, { useEffect, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchMe } from './store/slices/authSlice';
import ErrorBoundary from './components/ErrorBoundary';

import Layout from './components/layout/Layout';

// Lazy-loaded pages (code splitting)
const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const TrackDetail = lazy(() => import('./pages/TrackDetail'));
const VideoWatch = lazy(() => import('./pages/VideoWatch'));
const QuizPage = lazy(() => import('./pages/QuizPage'));
const MyCertificates = lazy(() => import('./pages/MyCertificates'));
const Cursos = lazy(() => import('./pages/Cursos'));
const Treinamentos = lazy(() => import('./pages/Treinamentos'));
const Perfil = lazy(() => import('./pages/Perfil'));
const CertificateView = lazy(() => import('./pages/CertificateView'));
const VerifyCertificate = lazy(() => import('./pages/VerifyCertificate'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const Register = lazy(() => import('./pages/Register'));
const EsqueciSenha = lazy(() => import('./pages/EsqueciSenha'));
const RedefinirSenha = lazy(() => import('./pages/RedefinirSenha'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const ConsultingPage = lazy(() => import('./pages/ConsultingPage'));
const NewsletterPage = lazy(() => import('./pages/NewsletterPage'));
const SubscriptionSuccess = lazy(() => import('./pages/SubscriptionSuccess'));
const Empresa = lazy(() => import('./pages/Empresa'));
const ConvitePage = lazy(() => import('./pages/ConvitePage'));

const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminTracks = lazy(() => import('./pages/admin/AdminTracks'));
const AdminTrackEdit = lazy(() => import('./pages/admin/AdminTrackEdit'));
const AdminComments = lazy(() => import('./pages/admin/AdminComments'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminEmpresas = lazy(() => import('./pages/admin/AdminEmpresas'));

const PageLoader = () => (
  <div className="text-center py-5">
    <div className="spinner-border" style={{ color: 'var(--gold)' }}></div>
  </div>
);

function PrivateRoute({ children, adminOnly = false }) {
  const { user, token } = useSelector((s) => s.auth);
  const location = useLocation();
  if (!token || !user) {
    // Deslogado na raiz vê a landing, como sempre. Em qualquer OUTRA rota, o destino é
    // preservado no ?next e devolvido depois do login — sem isto, o link do convite de
    // empresa (que chega por e-mail para quem normalmente NEM tEM conta) caía na landing
    // e o token do convite sumia da URL: o fluxo principal de entrada de um assento
    // morria em silêncio.
    const destino = `${location.pathname}${location.search || ''}`;
    if (destino === '/' || destino === '') return <Navigate to="/bem-vindo" replace />;
    return <Navigate to={`/login?next=${encodeURIComponent(destino)}`} replace />;
  }
  if (adminOnly && user.role !== 'admin') return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  const dispatch = useDispatch();
  const { token } = useSelector((s) => s.auth);

  useEffect(() => {
    if (token) dispatch(fetchMe());
  }, [dispatch, token]);

  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/bem-vindo" element={<LandingPage />} />
          <Route path="/cadastro" element={<Register />} />
          <Route path="/login" element={<Login />} />
          {/* Fora do PrivateRoute e em PAGINAS_PUBLICAS (api.js): quem chega aqui não
              tem sessão válida, e o link do e-mail não pode ser trocado pelo login. */}
          <Route path="/esqueci-senha" element={<EsqueciSenha />} />
          <Route path="/redefinir-senha" element={<RedefinirSenha />} />
          <Route path="/verificar/:code" element={<VerifyCertificate />} />

          <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
            <Route index element={<Dashboard />} />
            <Route path="trilhas/:id" element={<TrackDetail />} />
            <Route path="videos/:id" element={<VideoWatch />} />
            <Route path="quiz/:trackId" element={<QuizPage />} />
            <Route path="cursos" element={<Cursos />} />
            <Route path="treinamentos" element={<Treinamentos />} />
            <Route path="meus-certificados" element={<MyCertificates />} />
            {/* O resgate de codigo deixou de ser pagina propria e virou acao dentro de
                Treinamentos; o redirect mantem vivos os links antigos de e-mail. */}
            <Route path="resgatar-codigo" element={<Navigate to="/treinamentos" replace />} />
            <Route path="perfil" element={<Perfil />} />
            <Route path="certificado/:id" element={<CertificateView />} />
            <Route path="assinatura" element={<PricingPage />} />
            <Route path="assinatura/sucesso" element={<SubscriptionSuccess />} />
            {/* A pagina deixou de ser so consultoria e virou o canal unico de contato
                (treinamento personalizado, duvida de empresa). "fale-com-a-ndm" e o
                endereco novo; "consultorias" continua respondendo porque esta em
                e-mails antigos e em links que ja circularam. */}
            <Route path="fale-com-a-ndm" element={<ConsultingPage />} />
            <Route path="consultorias" element={<ConsultingPage />} />
            <Route path="templates" element={<Navigate to="/fale-com-a-ndm" replace />} />
            <Route path="newsletter" element={<NewsletterPage />} />

            {/* Empresa.jsx e quem decide o que mostrar: gestor, colaborador ou o aviso de
                'sem_empresa' vindo do 403. Por isso a rota nao ganha guard por papel — um
                guard aqui erraria enquanto /auth/me ainda nao respondeu. */}
            <Route path="empresa" element={<Empresa />} />
            <Route path="convite/:token" element={<ConvitePage />} />

            {/* Admin */}
            <Route path="admin" element={<PrivateRoute adminOnly><AdminDashboard /></PrivateRoute>} />
            <Route path="admin/trilhas" element={<PrivateRoute adminOnly><AdminTracks /></PrivateRoute>} />
            <Route path="admin/trilhas/:id" element={<PrivateRoute adminOnly><AdminTrackEdit /></PrivateRoute>} />
            <Route path="admin/comentarios" element={<PrivateRoute adminOnly><AdminComments /></PrivateRoute>} />
            <Route path="admin/usuarios" element={<PrivateRoute adminOnly><AdminUsers /></PrivateRoute>} />
            <Route path="admin/empresas" element={<PrivateRoute adminOnly><AdminEmpresas /></PrivateRoute>} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
